import { useState, useCallback } from "react";
import {
  getConversations,
  createConversation,
  deleteConversation,
  getConversationMessages,
  sendMessage,
  markConversationRead,
  archiveConversation,
  reopenConversation,
  getConversationStats,
  getUnreadConversations,
} from "../../../../api/marketplace";

export function useMessaging() {
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [stats, setStats] = useState(null);
  const [unread, setUnread] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchConversations = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getConversations(params);
      setConversations(res.data);
    } catch (err) {
      setError(err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getConversationMessages(id);
      setMessages(res.data);
    } catch (err) {
      setError(err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await getConversationStats();
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch conversation stats:", err);
    }
  }, []);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await getUnreadConversations();
      setUnread(res.data.conversations || []);
    } catch (err) {
      console.error("Failed to fetch unread conversations:", err);
    }
  }, []);

  const create = useCallback(async (data) => {
    const res = await createConversation(data);
    await fetchConversations();
    return res.data;
  }, [fetchConversations]);

  const send = useCallback(async (conversationId, body) => {
    const res = await sendMessage(conversationId, { body });
    await fetchMessages(conversationId);
    await fetchConversations();
    return res.data;
  }, [fetchMessages, fetchConversations]);

  const markRead = useCallback(async (id) => {
    const res = await markConversationRead(id);
    await fetchConversations();
    await fetchUnread();
    return res.data;
  }, [fetchConversations, fetchUnread]);

  const archive = useCallback(async (id) => {
    const res = await archiveConversation(id);
    await fetchConversations();
    return res.data;
  }, [fetchConversations]);

  const reopen = useCallback(async (id) => {
    const res = await reopenConversation(id);
    await fetchConversations();
    return res.data;
  }, [fetchConversations]);

  const remove = useCallback(async (id) => {
    const res = await deleteConversation(id);
    await fetchConversations();
    return res.data;
  }, [fetchConversations]);

  return {
    conversations,
    messages,
    activeConversation,
    stats,
    unread,
    loading,
    error,
    fetchConversations,
    fetchMessages,
    fetchStats,
    fetchUnread,
    create,
    send,
    markRead,
    archive,
    reopen,
    remove,
    setActiveConversation,
    setMessages,
  };
}
